import {
    defaultSyliusImageType,
    GetProductResponse,
    GetProductVariantResponse,
    syliusFetch,
} from '../../../entities/@api/Sylius';
import {
    transformSlugToVariantId,
    transformToProductBreadcrumb,
    transformToProductImages,
    transformToVariantOption,
} from '../../../entities/@products/Product/ProductTransformers';
import { sortVariantsOptions, transformToProductDefault } from '../../../entities/@products/ProductDefault/ProductDefaultTransformers';
import { transformToProductVariant } from '../../../entities/@products/ProductVariant/ProductVariantTransformers';
import { fetchTaxonHierarchy } from '../../../entities/@shop/Taxon/TaxonService';
import { isFetchResultSuccessful } from '../../../entities/FetchResult/FetchResult';
import { Link } from '../../../entities/Link/Link';
import { transformTaxonToLink } from '../../../entities/Link/LinkTransformers';
import { ReduxFetchAction } from '../../defaults';
import {
    setBreadcrumbs,
    setError,
    setHasFetched,
    setIsLoadingProductDefault,
    setIsLoadingProductVariant,
    setProductDefault,
    setProductVariant,
} from './productSlice';

export const fetchProductFromRelativeUrl: ReduxFetchAction<string> = relativeUrl => async dispatch => {
    dispatch(setIsLoadingProductDefault(true));
    dispatch(setIsLoadingProductVariant(true));
    dispatch(setHasFetched(false));
    dispatch(setError(''));
    dispatch(setProductDefault(undefined));

    try {
        const variantId = transformSlugToVariantId(relativeUrl);
        const productVariantResponse = await syliusFetch<GetProductVariantResponse>(`/shop/product-variants/${variantId}`);

        if (!isFetchResultSuccessful(productVariantResponse)) {
            dispatch(setError(productVariantResponse.error));
            return;
        }

        const productVariantResource = productVariantResponse.data;
        const productVariant = transformToProductVariant(productVariantResource);
        dispatch(setProductVariant(productVariant));
        dispatch(setIsLoadingProductVariant(false));

        const imageFilterQuery = `?imageFilter=${defaultSyliusImageType}`;
        const productDefaultResponse = await syliusFetch<GetProductResponse>(`/shop/products/${productVariant.productCode}${imageFilterQuery}`);

        if (!isFetchResultSuccessful(productDefaultResponse)) {
            dispatch(setError(productDefaultResponse.error));
            return;
        }

        const productDefaultResource = productDefaultResponse.data;

        const taxonHierarchy = await fetchTaxonHierarchy(productDefaultResource.mainTaxon || '');

        const taxonLink = taxonHierarchy
            ? transformTaxonToLink(taxonHierarchy)
            : undefined;

        const taxonParentLinks: Link[] = taxonHierarchy?.parents
            ? taxonHierarchy.parents.map(parent => ({
                href: parent.slug,
                label: parent.name,
            }))
            : [];

        const breadcrumbBases = taxonLink
            ? [...taxonParentLinks, taxonLink]
            : taxonParentLinks;

        const breadcrumbs = breadcrumbBases.map((link, index) => (
            transformToProductBreadcrumb(breadcrumbBases, link, index)
        ));

        dispatch(setBreadcrumbs(breadcrumbs));

        const categories = breadcrumbs.map(link => link.label);

        const variants = productDefaultResource.variantReferences.length
            ? productDefaultResource.variantReferences.map(transformToVariantOption)
            : [];

        const sortedVariants = variants.length
            ? sortVariantsOptions(variants, relativeUrl)
            : [];

        const productDefaultImages = productDefaultResource.images;
        const productVariantImageIds = productVariantResource.images;

        // Only show images of this specific variant
        const images = transformToProductImages(productDefaultImages, productVariantImageIds);

        const productDefault = transformToProductDefault(productDefaultResource, {
            variantID: productVariant.variantCode,
            variants: sortedVariants,
            images,
        });

        dispatch(setProductDefault({ ...productDefault, categories }));
    } catch (error) {
        console.error('[fetchProductFromRelativeUrl]', error);
    } finally {
        dispatch(setIsLoadingProductDefault(false));
        dispatch(setIsLoadingProductVariant(false));
        dispatch(setHasFetched(true));
    }
};
